import { Active, Over } from "@dnd-kit/core/dist/store/types";
import { ExplorerItem, ExplorerItemId, ExplorerItems } from "@/types/types.ts";
import { arrayMove } from "@dnd-kit/sortable";
import { GridRowId, GridValidRowModel } from "@mui/x-data-grid-pro";
import { UniqueIdentifier } from "@dnd-kit/core"
import {
  apiMoveItemsToFolder, apiMoveItemToFolder, changeExplorerItemsOrder, changeExportedSegmentTracksOrder
} from "@/services/api/track.ts";
import { EXPLORER_ROOT } from "@/features/Explorer/Explorer.tsx";

export const dropIntoList = (
  active: Active,
  overId: string,
  selectedRowIds: GridRowId[] & string[],
  setExplorerItems: React.Dispatch<React.SetStateAction<ExplorerItems>>,
  setUpdateCompleteId: React.Dispatch<React.SetStateAction<null | string | UniqueIdentifier>>,
) => {
  if (selectedRowIds.length > 1) {
    uiMoveItemsToList(active, overId, setExplorerItems, selectedRowIds, setUpdateCompleteId)
    apiMoveItemsToFolder(selectedRowIds, overId as string,)
  } else {
    uiMoveItemToList(active, overId, setExplorerItems, setUpdateCompleteId)
    apiMoveItemToFolder(active.id as string, overId)
  }
}

export const dropIntoRoot = (
  active: Active,
  selectedRowIds: GridRowId[] & string[],
  setExplorerItems: React.Dispatch<React.SetStateAction<ExplorerItems>>,
  setUpdateCompleteId: React.Dispatch<React.SetStateAction<null | string | UniqueIdentifier>>,
) => {
  if (selectedRowIds.length > 1) {
    uiMoveItemsToList(active, EXPLORER_ROOT, setExplorerItems, selectedRowIds, setUpdateCompleteId)
    apiMoveItemsToFolder(selectedRowIds, EXPLORER_ROOT as string,)
  } else {
    uiMoveItemToList(active, EXPLORER_ROOT, setExplorerItems, setUpdateCompleteId)
    apiMoveItemToFolder(active.id as string, EXPLORER_ROOT)
  }
}

export const uiReorderItem = (
  active: Active,
  over: Over,
  explorerItems: ExplorerItems,
  setExplorerItems: React.Dispatch<React.SetStateAction<ExplorerItems>>,
  rows: GridValidRowModel[],
  deltaY: number,
): ExplorerItems => {
  const itemsArray = Object.values(rows);
  const oldIndex = itemsArray.findIndex((item) => item.id === active.id);
  let newIndex = itemsArray.findIndex((item) => item.id === over.id);

  // Pas alleen de index aan als we niet aan de uiterste grenzen zitten
  if (deltaY > 0 && newIndex > 0) {
    newIndex = newIndex - 1;
  } else if (deltaY <= 0 && newIndex < itemsArray.length - 1) {
    newIndex = newIndex + 1;
  }

  const newItemsArray = arrayMove(itemsArray, oldIndex, newIndex);

  // Stel de `sortOrder` in op basis van de index in `newItemsArray`
  const newItems = newItemsArray.reduce((acc, item, index) => {
    acc[item.id] = {
      ...item,
      sortOrder: index,
    };
    return acc;
  }, {} as ExplorerItems);

  const explorerItem = explorerItems[active.id];
  if (!explorerItem) {
    return explorerItems;
  }

  // Filter items die niet tot dezelfde parent behoren
  const itemsFiltered = Object.values(explorerItems).filter(
    (item) => item.explorerItemParentId !== explorerItem.explorerItemParentId
  );

  const itemsFilteredCopy = itemsFiltered.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {} as ExplorerItems);

  setExplorerItems({
    ...itemsFilteredCopy,
    ...newItems,
  });

  return newItems;
};

export const uiReorderItems = (
  active: Active,
  over: Over,
  explorerItems: ExplorerItems,
  setExplorerItems: React.Dispatch<React.SetStateAction<ExplorerItems>>,
  selectedItems: ExplorerItem[],
  rows: GridValidRowModel[],
  deltaY: number,
): ExplorerItems => {
  const itemsArray = Object.values(rows);
  const activeIndex = active.data?.current?.sortable?.index || 0;
  const overIndex = over.data?.current?.sortable.index;
  const selectedRowIds = selectedItems.map(item => item.id);
  const nonSelectedItems = itemsArray.filter((item) => !selectedRowIds.includes(item.id));

  let insertIndex = nonSelectedItems.findIndex((item) => item.id === over.id);

  // Pas alleen de index aan als we niet aan de uiterste grenzen zitten
  if (deltaY > 0 && insertIndex > 0) {
    insertIndex = insertIndex - 1;
  } else if (deltaY <= 0 && insertIndex < nonSelectedItems.length - 1) {
    insertIndex = insertIndex + 1;
  }

  if (overIndex > activeIndex) {
    insertIndex++;
  }

  // Zorg ervoor dat insertIndex binnen geldige grenzen blijft
  insertIndex = Math.max(0, Math.min(insertIndex, nonSelectedItems.length));

  // Create the new array with the selected items moved to the new index
  const newItemsArray = [
    ...nonSelectedItems.slice(0, insertIndex),
    ...selectedItems,
    ...nonSelectedItems.slice(insertIndex),
  ].map((item, index) => ({
    ...item,
    sortOrder: index
  })) as ExplorerItems | GridValidRowModel;

  const newItems = newItemsArray.reduce((acc: ExplorerItems, item: ExplorerItem) => {
    acc[item.id] = item;
    return acc;
  }, {});

  const explorerItem = explorerItems[active.id];

  if (!explorerItem) {
    return explorerItems;
  }

  const itemsFiltered = Object.values(explorerItems).filter(
    (item) => item.explorerItemParentId !== explorerItem.explorerItemParentId
  );

  const itemsFilteredCopy = itemsFiltered.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {} as ExplorerItems);

  setExplorerItems({
    ...itemsFilteredCopy,
    ...newItems,
  });

  return newItems;
}

export const apiReorderItems = (idList: ExplorerItemId[], uniqueTabId: string) => {
  return changeExplorerItemsOrder(idList, uniqueTabId)
}

export const apiReorderTrackSegments = (idList: number[], uniqueTabId: string) => {
  return changeExportedSegmentTracksOrder(idList, uniqueTabId)
}

export const uiMoveItemsToList = (
  active: Active,
  overId: string,
  setExplorerItems: React.Dispatch<React.SetStateAction<ExplorerItems>>,
  selectedRowIds: GridRowId[],
  setUpdateCompleteId: React.Dispatch<React.SetStateAction<UniqueIdentifier | null>>,
) => {
  setExplorerItems((items) => {
    const updatedItems = Object.keys(items).reduce((acc, key) => {
      const item = { ...items[key] };
      if (item.id === active.id || selectedRowIds.includes(item.id)) {
        item.explorerItemParentId = overId
      }
      acc[key] = item;
      return acc;
    }, {} as ExplorerItems);

    if (overId) {
      setUpdateCompleteId(overId);
    }

    return updatedItems;
  });
}

export const uiMoveItemToList = (
  active: Active,
  overId: string,
  setExplorerItems: React.Dispatch<React.SetStateAction<ExplorerItems>>,
  setUpdateCompleteId: React.Dispatch<React.SetStateAction<UniqueIdentifier | null>>,
) => {
  setExplorerItems((items) => {
    const updatedItems = Object.keys(items).reduce((acc, key) => {
      const item = { ...items[key] };
      if (item.id === active.id) {
        item.explorerItemParentId = overId // finally, set new parent to the item itself
      }
      acc[key] = item;
      return acc;
    }, {} as ExplorerItems);

    if (overId) {
      setUpdateCompleteId(overId);
    }

    return updatedItems;
  });
}
